/**
 * Bobbin Connect — browser-side of the desktop-app login flow.
 *
 * Reached via /connect/bobbin?redirect_uri=...&state=...&app=bobbin.
 * After the user is signed in to Thread, this page validates the
 * redirect URI is loopback, shows a small confirmation dialog
 * ("Connect Bobbin on this machine?"), then on Connect calls
 * POST /api/connect/bobbin/mint and 302s to the loopback callback.
 *
 * Not OAuth — the trust boundary is "user has a Thread session in this
 * browser" + "redirect URI is loopback". See bobbin_connect.py on the
 * backend.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authClient } from "../lib/auth";
import { apiClient } from "../api/client";

type Status =
  | "checking_auth"
  | "needs_signin"
  | "needs_confirm"
  | "minting"
  | "redirecting"
  | "error";

const LOOPBACK_HOSTS = new Set(["127.0.0.1", "localhost", "::1"]);

/**
 * Validate that *redirectUri* is a loopback HTTP URL. Returns
 * ``{ ok: true, host, port }`` on success or ``{ ok: false, reason }``
 * on failure. Mirrors ``validate_loopback_redirect_uri`` in
 * ``bobbin_connect.py`` — keep them in sync.
 */
export function validateLoopbackRedirectUri(
  redirectUri: string | null | undefined,
): { ok: true; host: string; port: number } | { ok: false; reason: string } {
  if (!redirectUri) {
    return { ok: false, reason: "redirect_uri is required" };
  }
  let parsed: URL;
  try {
    parsed = new URL(redirectUri);
  } catch {
    return { ok: false, reason: "redirect_uri is not a valid URL" };
  }
  if (parsed.protocol !== "http:") {
    return {
      ok: false,
      reason: `redirect_uri scheme must be http (got ${parsed.protocol}); loopback URIs cannot use https`,
    };
  }
  if (parsed.username || parsed.password) {
    return { ok: false, reason: "redirect_uri must not contain userinfo" };
  }
  if (parsed.hash) {
    return { ok: false, reason: "redirect_uri must not contain a fragment" };
  }
  // URL.hostname lowercases the host but keeps IPv6 brackets (unlike
  // Python's urlparse). Strip them so the comparison matches the
  // backend's allow-list.
  const host = parsed.hostname.replace(/^\[(.+)\]$/, "$1");
  if (!LOOPBACK_HOSTS.has(host)) {
    return {
      ok: false,
      reason: `redirect_uri host "${host}" is not a loopback address; only 127.0.0.1, localhost, or ::1 are allowed`,
    };
  }
  if (!parsed.port) {
    return { ok: false, reason: "redirect_uri must include an explicit port" };
  }
  const port = Number(parsed.port);
  if (!Number.isInteger(port) || port < 1024 || port > 65535) {
    return {
      ok: false,
      reason: `redirect_uri port ${parsed.port} is outside the allowed range (1024-65535)`,
    };
  }
  return { ok: true, host, port };
}

type MintResponse = {
  token: string;
  user_id: string;
  email: string | null;
  redirect_url: string;
  expires_at: string;
};

export default function BobbinConnect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const redirectUri = searchParams.get("redirect_uri");
  const state = searchParams.get("state");
  const app = searchParams.get("app") ?? "bobbin";

  const validation = useMemo(
    () => validateLoopbackRedirectUri(redirectUri),
    [redirectUri],
  );

  const [status, setStatus] = useState<Status>("checking_auth");
  const [error, setError] = useState<string | null>(null);

  // ── Step 1: auth check ──────────────────────────────────────────────
  useEffect(() => {
    if (!validation.ok) {
      setError(validation.reason);
      setStatus("error");
      return;
    }
    if (!state) {
      setError("state parameter is required");
      setStatus("error");
      return;
    }

    async function checkAuth() {
      const {
        data: { session },
      } = await authClient.auth.getSession();
      if (!session) {
        // Stash the full return URL (including the `/thread` basename in
        // prod) under the same key used by the existing CLI flow. We store
        // the absolute pathname rather than a router-internal path so that
        // AuthCallback can bounce back via window.location.assign() — that
        // sidesteps React Router's basename handling, which was dropping the
        // `/thread` prefix when navigate() was called with a string that
        // contained query params after sign-in. See AuthCallback.tsx.
        const fullPath = `${window.location.pathname}${window.location.search}`;
        sessionStorage.setItem("cli_auth_return", fullPath);
        setStatus("needs_signin");
        navigate("/", { replace: true });
        return;
      }
      setStatus("needs_confirm");
    }
    checkAuth();
  }, [validation, state, navigate]);

  // ── Step 2: user clicks Connect → mint + redirect ────────────────────
  async function handleConnect() {
    if (!validation.ok || !state || !redirectUri) return;
    setStatus("minting");
    setError(null);
    try {
      const resp = await apiClient<MintResponse>("/connect/bobbin/mint", {
        method: "POST",
        body: { redirect_uri: redirectUri, state, app },
      });
      setStatus("redirecting");
      // Browsers permit https → http navigations to loopback even though
      // they block fetch (mixed content). This is the documented escape
      // hatch — same trick CLIAuthPage uses.
      window.location.href = resp.redirect_url;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to connect";
      setError(message);
      setStatus("error");
    }
  }

  function handleCancel() {
    navigate("/", { replace: true });
  }

  // ── Render ──────────────────────────────────────────────────────────
  const appLabel = app.charAt(0).toUpperCase() + app.slice(1);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: 24,
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: "100%",
          background: "var(--gray-2, #ffffff)",
          border: "1px solid var(--gray-5, #e5e7eb)",
          borderRadius: 12,
          padding: "32px 28px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        {status === "checking_auth" && (
          <p style={{ color: "var(--gray-11, #6b7280)", margin: 0 }}>
            Checking your Thread session…
          </p>
        )}

        {status === "needs_signin" && (
          <p style={{ color: "var(--gray-11, #6b7280)", margin: 0 }}>
            Redirecting to sign-in…
          </p>
        )}

        {status === "needs_confirm" && validation.ok && (
          <>
            <h1
              style={{
                fontSize: 20,
                fontWeight: 600,
                margin: "0 0 12px",
                color: "var(--gray-12, #111827)",
              }}
            >
              Connect {appLabel} to your Thread account?
            </h1>
            <p
              style={{
                fontSize: 14,
                color: "var(--gray-11, #6b7280)",
                margin: "0 0 8px",
                lineHeight: 1.5,
              }}
            >
              {appLabel} is asking to sign in on this machine. Approving will
              create a 30-day access token and send it to{" "}
              <code style={{ background: "var(--gray-3, #f3f4f6)", padding: "2px 6px", borderRadius: 4, fontSize: 13 }}>
                {validation.host}:{validation.port}
              </code>
              .
            </p>
            <p
              style={{
                fontSize: 13,
                color: "var(--gray-10, #9ca3af)",
                margin: "0 0 24px",
                lineHeight: 1.5,
              }}
            >
              You can revoke this token anytime from Settings → API keys.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "1px solid var(--gray-6, #d1d5db)",
                  background: "transparent",
                  color: "var(--gray-12, #111827)",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConnect}
                style={{
                  padding: "8px 18px",
                  borderRadius: 8,
                  border: "1px solid var(--blue-9, #2563eb)",
                  background: "var(--blue-9, #2563eb)",
                  color: "white",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Connect
              </button>
            </div>
          </>
        )}

        {status === "minting" && (
          <p style={{ color: "var(--gray-11, #6b7280)", margin: 0 }}>
            Minting access token…
          </p>
        )}

        {status === "redirecting" && (
          <>
            <div style={{ fontSize: 40, marginBottom: 12 }}>&#x2713;</div>
            <p
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "var(--green-9, #16a34a)",
                margin: "0 0 8px",
              }}
            >
              Connected. You can close this tab.
            </p>
            <p style={{ fontSize: 13, color: "var(--gray-11, #6b7280)", margin: 0 }}>
              {appLabel} should be ready to use now.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <h1
              style={{
                fontSize: 18,
                fontWeight: 600,
                margin: "0 0 12px",
                color: "var(--red-11, #b91c1c)",
              }}
            >
              Connection failed
            </h1>
            <p
              style={{
                fontSize: 14,
                color: "var(--gray-12, #111827)",
                margin: "0 0 16px",
                lineHeight: 1.5,
              }}
            >
              {error}
            </p>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid var(--gray-6, #d1d5db)",
                background: "var(--gray-3, #f3f4f6)",
                color: "var(--gray-12, #111827)",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Back to Thread
            </button>
          </>
        )}
      </div>
    </div>
  );
}
